import React, { useState, useEffect } from "react";
import { Button, FormInput, PasswordInput, FormSelect } from "../Common.jsx";
import { DynamicIcon } from "../icons.jsx";

export default function DbConfigForm({ data, onSave, onNext }) {
  const [form, setForm] = useState({
    db_tier: data?.db_tier ?? "dedicated_db",
    storage_tier: data?.storage_tier ?? "standard",
    encryption_enabled: data?.encryption_enabled ?? true,
    backup_frequency: data?.backup_frequency ?? "daily",
    db_username: data?.db_username ?? "",
    db_password: data?.db_password ?? "",
  });

  useEffect(() => {
    setForm(prev => ({ ...prev, ...data }));
  }, [data]);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const sanitize = (d) => ({
    ...d,
    db_username: d.db_username?.trim() || null,
    db_password: d.db_password?.trim() || null,
  });

  return (
    <div className="max-w-5xl space-y-8">

      {/* Tier Selection */}
      {/* Tier Selection */}
      <h3 className="font-bold text-gray-700 dark:text-gray-300">Database Isolation Tier</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* 1. SHARED SCHEMA */}
        <div
          className={`p-5 rounded-xl border-2 cursor-pointer transition-all flex flex-col ${form.db_tier === 'shared_schema' ? 'border-primary bg-primary/5 shadow-md' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'}`}
          onClick={() => update("db_tier", "shared_schema")}
        >
          <div className="flex justify-between items-start mb-4">
            <div className="p-3 bg-green-100 text-green-600 rounded-lg"><DynamicIcon name="Users" className="w-6 h-6" /></div>
            {form.db_tier === 'shared_schema' && <DynamicIcon name="CheckCircle" className="text-primary w-6 h-6" />}
          </div>
          <h3 className="font-bold text-base mb-1">Shared Schema</h3>
          <p className="text-xs text-gray-500 mt-auto">Data commingled in shared tables (Row-Level Security). Most cost efficient.</p>
        </div>

        {/* 2. DEDICATED SCHEMA */}
        <div
          className={`p-5 rounded-xl border-2 cursor-pointer transition-all flex flex-col ${form.db_tier === 'dedicated_schema' ? 'border-primary bg-primary/5 shadow-md' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'}`}
          onClick={() => update("db_tier", "dedicated_schema")}
        >
          <div className="flex justify-between items-start mb-4">
            <div className="p-3 bg-blue-100 text-blue-600 rounded-lg"><DynamicIcon name="Layers" className="w-6 h-6" /></div>
            {form.db_tier === 'dedicated_schema' && <DynamicIcon name="CheckCircle" className="text-primary w-6 h-6" />}
          </div>
          <h3 className="font-bold text-base mb-1">Dedicated Schema</h3>
          <p className="text-xs text-gray-500 mt-auto">Separate schema within a shared database cluster. Good balance of isolation.</p>
        </div>

        {/* 3. DEDICATED DATABASE */}
        <div
          className={`p-5 rounded-xl border-2 cursor-pointer transition-all flex flex-col ${form.db_tier === 'dedicated_db' ? 'border-primary bg-primary/5 shadow-md' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'}`}
          onClick={() => update("db_tier", "dedicated_db")}
        >
          <div className="flex justify-between items-start mb-4">
            <div className="p-3 bg-purple-100 text-purple-600 rounded-lg"><DynamicIcon name="Server" className="w-6 h-6" /></div>
            {form.db_tier === 'dedicated_db' && <DynamicIcon name="CheckCircle" className="text-primary w-6 h-6" />}
          </div>
          <h3 className="font-bold text-base mb-1">Dedicated DB</h3>
          <p className="text-xs text-gray-500 mt-auto">Fully isolated database instance. Maximum security and performance.</p>
        </div>

      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border p-6 space-y-4">
        <h3 className="font-bold text-gray-700 dark:text-gray-300">Credentials & Security</h3>
        <div className="grid grid-cols-2 gap-4">
          <FormInput label="Master Username" value={form.db_username} onChange={(e) => update("db_username", e.target.value)} />
          <div>
            <PasswordInput label="Master Password" value={form.db_password} onChange={(e) => update("db_password", e.target.value)} />
            <p className="text-xs text-blue-500 mt-1 flex items-start gap-1">
              <DynamicIcon name="Info" className="w-3 h-3 mt-0.5 shrink-0" />
              <span>
                Suggestion: Use a strong password (at least 8 chars, 1 uppercase, 1 symbol). This is not strictly enforced for database credentials.
              </span>
            </p>
          </div>
        </div>
        <div className="flex gap-6 pt-2">
          <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={form.encryption_enabled} onChange={(e) => update("encryption_enabled", e.target.checked)} /> Enable Storage Encryption</label>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border p-6 space-y-4">
        <h3 className="font-bold text-gray-700 dark:text-gray-300">Operations</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Backup Frequency</label>
            <select className="w-full p-2 border rounded bg-transparent" value={form.backup_frequency} onChange={(e) => update("backup_frequency", e.target.value)}>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="hourly">Hourly (Critical)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Storage Tier</label>
            <select className="w-full p-2 border rounded bg-transparent" value={form.storage_tier} onChange={(e) => update("storage_tier", e.target.value)}>
              <option value="standard">Standard SSD</option>
              <option value="provisioned_iops">Provisioned IOPS</option>
            </select>
          </div>
        </div>
      </div>


      <div className="sticky bottom-0 z-10 bg-white/90 dark:bg-gray-800/90 backdrop-blur border-t dark:border-gray-700 p-4 -mx-6 -mb-6 mt-8 flex justify-end shadow-lg rounded-b-xl">
        <Button onClick={async () => { await onSave(form); if (onNext) onNext(); }} className="px-6 shadow-md">
          Save & Next <DynamicIcon name="ArrowRight" className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
